import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, RefreshCw, Mail, Phone, Bike, FileText, Calendar, Users, Briefcase, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout, getToken } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, b2b: 0, per_model: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/leads/stats`, { headers }),
      ]);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data || { total: 0, b2b: 0, per_model: [] });
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Nie udało się pobrać danych.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const handleLogout = () => {
    logout();
    toast.success("Wylogowano.");
    navigate("/admin/login", { replace: true });
  };

  const handleExportCsv = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/admin/leads/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
      link.setAttribute("download", `celestia-leads-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Pobrano CSV.");
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Nie udało się pobrać CSV.");
    }
  };

  const filtered = leads.filter((l) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (l.email || "").toLowerCase().includes(q) ||
      (l.phone || "").toLowerCase().includes(q) ||
      (l.scooter_model || "").toLowerCase().includes(q) ||
      (l.notes || "").toLowerCase().includes(q)
    );
  });

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("pl-PL");
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen space-bg" data-testid="admin-dashboard-root">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B0410]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[#B026FF] pulse-glow" />
            <h1 className="text-white font-black tracking-tight text-base sm:text-lg">
              Celestia <span className="text-[#B026FF]">Control</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-xs text-[#A69CBB]" data-testid="admin-user-email">
              {user?.email}
            </div>
            <button
              onClick={handleExportCsv}
              data-testid="admin-export-csv"
              className="btn-ghost-neon px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Export CSV</span>
            </button>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="btn-ghost-neon px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Odśwież</span>
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="btn-ghost-neon px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Wyloguj</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <StatCard
            icon={Users}
            label="Łącznie zapisów"
            value={stats.total}
            testid="stat-total"
          />
          <StatCard
            icon={Briefcase}
            label="Zgłoszenia B2B [SERWIS]"
            value={stats.b2b}
            testid="stat-b2b"
          />
          <div
            className="industrial-card armor-corners relative rounded-md p-6"
            data-testid="stat-models"
          >
            <span className="corner tl" /><span className="corner tr" />
            <span className="corner bl" /><span className="corner br" />
            <div className="flex items-center gap-2 text-[#B026FF] text-xs uppercase tracking-[0.2em] mb-3">
              <Bike className="w-4 h-4" /> Top modele
            </div>
            <div className="space-y-1.5">
              {stats.per_model.slice(0, 3).map((m, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-[#C9C1DA] truncate pr-3">{m.model}</span>
                  <span className="text-white font-semibold">{m.count}</span>
                </div>
              ))}
              {stats.per_model.length === 0 && (
                <span className="text-xs text-[#756B8A]">Brak danych</span>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
            <h2 className="text-2xl font-black text-white tracking-tight">
              Lista Early Bird
            </h2>
            <input
              data-testid="admin-search"
              placeholder="Szukaj po email, modelu, uwagach…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="bg-[#100913] border border-white/8 text-white text-sm rounded-md px-4 py-2.5 focus:outline-none focus:border-[#B026FF]/60 focus:ring-1 focus:ring-[#B026FF]/60 w-full sm:w-80"
            />
          </div>

          <div
            className="industrial-card rounded-md overflow-hidden"
            data-testid="admin-leads-table"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0F0913] text-[10px] uppercase tracking-[0.2em] text-[#756B8A]">
                    <th className="text-left px-5 py-3 font-medium">Data</th>
                    <th className="text-left px-5 py-3 font-medium">Email</th>
                    <th className="text-left px-5 py-3 font-medium">Telefon</th>
                    <th className="text-left px-5 py-3 font-medium">Model</th>
                    <th className="text-left px-5 py-3 font-medium">Uwagi</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-[#756B8A]" data-testid="admin-loading">
                        Ładowanie danych…
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-[#756B8A]" data-testid="admin-empty">
                        Brak zapisów — gdy ktoś się zarejestruje, pojawi się tutaj.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((l, i) => (
                      <tr
                        key={l.id || i}
                        className="border-t border-white/5 hover:bg-[#B026FF]/5 transition"
                        data-testid={`admin-lead-row-${i}`}
                      >
                        <td className="px-5 py-3 text-[#A69CBB] whitespace-nowrap">
                          <span className="inline-flex items-center gap-2">
                            <Calendar className="w-3.5 h-3.5 text-[#756B8A]" />
                            {formatDate(l.created_at)}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-white">
                          <span className="inline-flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-[#B026FF]" />
                            {l.email}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-[#C9C1DA]">
                          {l.phone ? (
                            <span className="inline-flex items-center gap-2">
                              <Phone className="w-3.5 h-3.5 text-[#756B8A]" />
                              {l.phone}
                            </span>
                          ) : (
                            <span className="text-[#756B8A]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-[#C9C1DA]">{l.scooter_model}</td>
                        <td className="px-5 py-3 text-[#A69CBB] max-w-[28ch]">
                          {l.notes ? (
                            <span className="inline-flex items-start gap-2">
                              <FileText className="w-3.5 h-3.5 text-[#756B8A] mt-0.5 shrink-0" />
                              <span className="truncate" title={l.notes}>
                                {l.notes}
                              </span>
                            </span>
                          ) : (
                            <span className="text-[#756B8A]">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="industrial-card armor-corners relative rounded-md p-6"
      data-testid={testid}
    >
      <span className="corner tl" /><span className="corner tr" />
      <span className="corner bl" /><span className="corner br" />
      <div className="flex items-center gap-2 text-[#B026FF] text-xs uppercase tracking-[0.2em]">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="text-4xl font-black text-white mt-3 tracking-tight">{value}</div>
    </div>
  );
}
